import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext, useFormState } from 'react-hook-form'
import { EditableCellProps } from './types'
import { Form, Select, Tooltip } from 'antd'
import UploadInput from '@shared/UploadInput'
import { InfoCircleOutlined } from '@ant-design/icons'
import { EditIco } from '../../../modules/CustomIcons'
import { InputField } from '@shared/InputField'
import styles from './EditableRow.module.scss'
import { useDebounceCallback } from '@shared/hooks/useDebounceCallback'
import IMask from 'imask'
import { useTranslation } from 'react-i18next'

const useMaskedValue = () => {
  return useCallback((value, maskOptions) => {
    const mask = IMask.createMask(maskOptions)
    mask.resolve(value)
    return mask.value
  }, [])
}

function Editable(props: EditableCellProps) {
  const {
    title,
    editable,
    children,
    dataIndex,
    selectOptions,
    record,
    isSelect,
    isFile,
    rules,
    maskOptions,
    index,
    type,
    ...restProps
  } = props
  const inputRef = useRef(null)
  const { control, setValue, getValues } = useFormContext()
  const { errors } = useFormState({
    control,
    name: `tableList.${record.id}.${dataIndex}`,
    exact: true,
  })

  const isValid = !errors?.tableList?.[record.id]?.[dataIndex]

  const [isEdit, setEdit] = useState(false)

  const maskedValue = useMaskedValue()
  const { t } = useTranslation()
  const debounced = useDebounceCallback(async (e) => {
    const value = maskOptions
      ? type === 'number'
        ? e.unmaskedValue.length > 0
          ? Number(e.unmaskedValue)
          : e.unmaskedValue
        : e.maskedValue
      : e

    setValue(`tableList.${record.id}.${dataIndex}`, value, {
      shouldValidate: true,
    })

    // const valid = await trigger(`tableList.${record.id}`);
    // setValue(`tableList.${record.id}.formStatus`, valid ? 'valid': 'invalid');
    // setValue(`formStatus.${record.id}.value`, valid ? 'valid': 'invalid');
  }, 350)

  useEffect(() => {
    if (isEdit && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEdit])

  const handleOnBlur = () => {
    setEdit(!isEdit)
  }

  if (isFile) {
    return (
      <Controller
        name={`tableList.${record.id}.${dataIndex}`}
        control={control}
        render={({ field: { onChange }, fieldState: { invalid, error } }) => {
          return (
            <UploadInput
              accept=".pdf"
              multiple={false}
              onUpload={(handleUpload) => {
                handleUpload(!invalid)
              }}
            />
          )
        }}
      />
    )
  }

  if (isSelect) {
    return (
      <Controller
        name={`tableList.${record.id}.${dataIndex}`}
        control={control}
        render={({ field, fieldState: { invalid, error } }) => {
          const { onChange, value, onBlur, ref, ...fields } = field

          return (
            <Form.Item
              style={{ margin: 0 }}
              className={`${styles.editTableCellInput} ${
                invalid ? styles.error : ''
              }`}
              help=""
              validateStatus=""
            >
              <Select
                size="small"
                {...field}
                onChange={(e) => debounced(e)}
                onSelect={handleOnBlur}
                onBlur={handleOnBlur}
                ref={inputRef}
                options={selectOptions}
                className={styles.selectBox}
                // open={isEdit}
              />
            </Form.Item>
          )
        }}
      />
    )
  }

  return !isEdit ? (
    <p
      className={`${styles.editTableCellInput} ${
        !isValid ? styles.error : ''
      } ant-row w-100`}
      style={{
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'text',
      }}
      onClick={handleOnBlur}
    >
      <Tooltip
        title={
          restProps?.ellipsis
            ? getValues(`tableList.${record.id}.${dataIndex}`)
            : ''
        }
      >
        <span>
          {maskOptions
            ? maskedValue(
                String(getValues(`tableList.${record.id}.${dataIndex}`)),
                { mask: maskOptions.mask, ...maskOptions.options }
              )
            : getValues(`tableList.${record.id}.${dataIndex}`)}
        </span>
      </Tooltip>
      <Tooltip
        color={'#f3646c'}
        key={index}
        title={
          !isValid
            ? t(errors?.tableList?.[record.id]?.[dataIndex]?.message)
            : ''
        }
      >
        {!isValid ? (
          <InfoCircleOutlined style={{ color: '#f3646c', cursor: 'pointer' }} />
        ) : (
          <EditIco />
        )}
      </Tooltip>
    </p>
  ) : (
    <Controller
      name={`tableList.${record.id}.${dataIndex}`}
      control={control}
      render={({ field, fieldState: { invalid, error } }) => {
        const { onChange, value, onBlur, ref, ...fields } = field

        return (
          <InputField
            ref={inputRef}
            help=""
            style={{ margin: 0 }}
            validateStatus="validating"
            className={`${styles.editTableCellInput} ${
              invalid ? styles.error : ''
            }`}
            {...(maskOptions
              ? { mask: maskOptions.mask, maskOptions: maskOptions.options }
              : {})}
            defaultValue={
              maskOptions
                ? maskedValue(String(value), {
                    mask: maskOptions.mask,
                    ...maskOptions.options,
                  })
                : value
            }
            autoComplete="off"
            size="small"
            onChange={(e) => debounced(e)}
            onBlur={handleOnBlur}
            suffix={
              <Tooltip
                color={'#f3646c'}
                key={index}
                title={invalid ? t(error?.message) : ''}
              >
                {invalid ? (
                  <InfoCircleOutlined style={{ color: '#f3646c' }} />
                ) : (
                  <EditIco />
                )}
              </Tooltip>
            }
            {...fields}
          />
        )
      }}
    />
  )
}

export default Editable
